import {
	ResponsiveContainer,
	ComposedChart,
	XAxis,
	YAxis,
	Bar,
	Line,
	LabelList,
	Tooltip,
	BarChart,
	Legend,
	Text,
	Area,
} from "recharts";
import { numberFormatter } from "../../utils";
import moment from "moment";
import divider from "../../assets/divider-logo.3699f955.svg";

interface DateRange {
  baseline: number | null;
  current: number | null;
}
interface Props {
	chartData: any;
	heading: any;
	page: any;
	dateRange?:DateRange
}

interface T {
	quarter: string;
	actual: number;
	forecastedSpend: number;
	spendNeededForNextTier: number;
	spendShift: number;
	targetGrowthSpend: number;
}

const CustomLabel = (props: any) => {
	const { x, y, value, index } = props.payload;
	const { page, dataLength } = props;
	const d = 20; // distance from point to label
	const angle = -110; // angle in degrees
	const angleRad = (angle * Math.PI) / 180; // convert to radians
	const xOffset = d * Math.cos(angleRad);
	const yOffset = d * Math.sin(angleRad);
	const shouldShow = index === 0 || index === dataLength - 1;
	if (!shouldShow) return null;
	return (
		<g>
			<line
				x1={x}
				y1={y}
				x2={x + xOffset}
				y2={y - yOffset}
				stroke="gray"
				opacity={0.5}
			/>
			<text
				x={x + xOffset}
				y={y - yOffset - 5}
				fill="gray"
				fontSize={`${page == "summary" ? "1.4vh" : "1.2vh"}`}
				opacity={`${page == "summary" ? 0.8 : 1}`}
				textAnchor="middle"
			>
				{numberFormatter(value, 1, "$")}
			</text>
		</g>
	);
};

const CustomLabel2 = (props: any) => {
	const { x, y, value, index } = props.payload;
	const { page, dataLength } = props;
	const d = 30; // distance from point to label
	const angle = 90; // angle in degrees
	const angleRad = (angle * Math.PI) / 180; // convert to radians
	const xOffset = d * Math.cos(angleRad);
	const yOffset = d * Math.sin(angleRad);
	const shouldShow = index === 0 || index === dataLength - 1;
	if (!shouldShow) return null;
	return (
		<g>
			<line
				x1={x}
				y1={y}
				x2={x + xOffset}
				y2={y - yOffset}
				stroke="#012169"
				opacity={0.5}
			/>
			<text
				x={x + xOffset}
				y={y - yOffset - 5}
				fill="#012169"
				fontSize={`${page == "summary" ? "1.4vh" : "1.2vh"}`}
				opacity={`${page == "summary" ? 0.8 : 1}`}
				textAnchor="middle"
			>
				{numberFormatter(value, 1, "$")}
			</text>
		</g>
	);
};

const SalesGrowth = (props: Props) => {
	let data: any = props?.chartData?.finalData;
	const { page } = props;
	let minValue = 0;
	let maxValue = 0;
	let yoy: any = props?.chartData?.yoy;
	let customTicks: any = [];
	let formattedData: any = [];

	if (data?.length > 0) {
		// Create formattedData with formattedWeek property
		formattedData = data.map((item: any) => ({
			...item,
			formattedWeek: item.monthly
				? moment.utc(item.week).format("MMM")
				: moment(item.week).format("MMM DD"),
		}));

		// Calculate min and max values
		const minPrev = formattedData.reduce((min: any, obj: any) => {
			return obj.revenue_Prev < min ? obj.revenue_Prev : min;
		}, Infinity);
		const minCurr = formattedData.reduce((min: any, obj: any) => {
			return obj.revenue_Curr < min ? obj.revenue_Curr : min;
		}, Infinity);
		const maxPrev = formattedData.reduce((max: any, obj: any) => {
			return obj.revenue_Prev > max ? obj.revenue_Prev : max;
		}, -Infinity);
		const maxCurr = formattedData.reduce((max: any, obj: any) => {
			return obj.revenue_Curr > max ? obj.revenue_Curr : max;
		}, -Infinity);

		minValue = minPrev > minCurr ? minCurr : minPrev;
		maxValue = maxPrev > maxCurr ? maxPrev : maxCurr;

		// Add greenRange and redRange to each data point
		formattedData = formattedData.map((item: any) => {
			const newItem = { ...item };
			if (item.revenue_Curr > item.revenue_Prev) {
				newItem.greenRange = [item.revenue_Prev, item.revenue_Curr];
				newItem.redRange = [item.revenue_Curr, item.revenue_Curr];
			} else if (item.revenue_Curr < item.revenue_Prev) {
				newItem.redRange = [item.revenue_Curr, item.revenue_Prev];
				newItem.greenRange = [item.revenue_Curr, item.revenue_Curr];
			} else {
				newItem.greenRange = [item.revenue_Curr, item.revenue_Curr];
				newItem.redRange = [item.revenue_Curr, item.revenue_Curr];
			}
			return newItem;
		});

		// Determine customTicks
		if (formattedData[0].monthly) {
			customTicks = formattedData.map(
				(item: { formattedWeek: any }) => item.formattedWeek
			);
		} else {
			const indicesToShow = [0]; // always show first
			// Add every third index starting from 0 + 3
			for (let i = 4; i < formattedData.length; i += 4) {
				indicesToShow.push(i);
			}
			customTicks = indicesToShow.map(
				(index) => formattedData[index].formattedWeek
			);
		}
	}

	let heading = props?.heading;

	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "fit-content",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				color: "black",
				padding: 0,
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-start h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">
							{data.monthly ? "Month" : "Week"}:
						</div>
						<div
							className="font-bold ml-1"
							style={{ color: "#3f3f3f" }}
						>
							{data.formattedWeek}
						</div>
					</div>

					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p className="label" style={{ padding: 0, margin: 0 }}>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Total Sales {props?.dateRange?.baseline||2024} :{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.revenue_Prev, 2, "$")}
							</label>
						</p>
						<p className="label" style={{ padding: 0, margin: 0 }}>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Total Sales {props?.dateRange?.current||2025} :{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.revenue_Curr, 2, "$")}
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`w-full graphBox h-[100%]`}>
			{/* <div
				className={`bg-[#ffff] graphBorder flex ${
					page == "summary" ? "" : "flex-col"
				} justify-between`}
			>
				<p className="text-center graphTitle pl-[1vh]">
					{" "}
					<span>{`${page == "summary" ? "4. " : "3. "}`}</span>
					{heading}{" "}
				</p>
				<p
					className="flex justify-end px-[2vh] font-semibold text-[1.8vh] mt-[0]"
					style={{ color: yoy > 0 ? "#40a108" : "#a10808" }}
				>
					Growth YOY : {numberFormatter(yoy * 100, 1)}%
				</p>
			</div> */}
			<div className="headSpace">
				<span className="headContent !text-[1vw]"> {heading}</span>
				<p
					className="flex justify-end px-[2vh] font-semibold textNormal mt-[0]"
					style={{ color: yoy > 0 ? "#40a108" : "#a10808" }}
				>
					Growth YOY : {numberFormatter(yoy * 100, 1)}%
				</p>
			</div>
			<ResponsiveContainer
				width="100%"
				height="65%"
				className="bg-[#ffff]"
			>
				<ComposedChart
					data={formattedData}
					margin={{
						top: 60,
						right: 40,
						left: 40,
					}}
				>
					<XAxis
						dataKey="formattedWeek"
						className="graph-label-small "
						stroke="black"
						interval={1}
						ticks={customTicks}
					/>
					<YAxis
						hide
						// domain={[minValue - (minValue / 4), maxValue]}
						domain={[
							(dataMin: any) => dataMin / 1.05,
							(dataMax: any) => dataMax * 0.98,
						]}
						fontSize="1.5vh"
						tickFormatter={(value: number) =>
							numberFormatter(value, 0, "$")
						}
					/>
					<Tooltip
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
						content={customTooltip}
					/>

					{/* Add Area components */}
					<Area
						dataKey="greenRange"
						stroke="#dce0a7"
						fill="#dce0a7"
						isAnimationActive={false}
					/>
					<Area
						dataKey="redRange"
						stroke="#ffcccc"
						fill="#ffcccc"
						isAnimationActive={false}
					/>
					<Line
						type="linear"
						dataKey="revenue_Prev"
						name={`2024`}
						stroke="gray"
						strokeDasharray={"0"}
						connectNulls={false}
						strokeWidth={2}
						// dot={page == 'summary' ? true : false}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={formattedData}
											color={"gray"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="revenue_Prev"
							content={($) => (
								<CustomLabel
									payload={$}
									page={page}
									dataLength={formattedData.length}
								/>
							)}
						/>
					</Line>
					<Line
						type="linear"
						dataKey="revenue_Curr"
						stroke="#012169"
						name={`2025`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={2}
						// dot={page == 'summary' ? true : false}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={formattedData}
											color={"#012169"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="revenue_Curr"
							content={($) => (
								<CustomLabel2
									payload={$}
									page={page}
									dataLength={formattedData.length}
								/>
							)}
						/>
					</Line>
				</ComposedChart>
			</ResponsiveContainer>
			<div className="contentFooter !justify-end">
				<CustomLegend dateRange={props?.dateRange}></CustomLegend>
			</div>
		</div>
	);
};

const CustomLegend = (props:any) => (
	<div
		style={{
			display: "flex",
			gap: "2rem",
			alignItems: "center",
			color: "black",
			fontSize: "0.8rem",
		}}
	>
		<span>
			<span
				style={{
					display: "inline-block",
					width: 10,
					height: 18,
					background: "gray",
					marginRight: 8,
					verticalAlign: "middle",
				}}
			/>
			{props?.dateRange?.baseline||2024}
		</span>
		<span>
			<span
				style={{
					display: "inline-block",
					width: 10,
					height: 18,
					background: "#012169",
					marginRight: 8,
					verticalAlign: "middle",
				}}
			/>
			{props?.dateRange?.current||2025}
		</span>
	</div>
);

const CustomDot = (props: any) => {
	const { cx, cy, index, data, color } = props;
	// Only render for first and last point
	if (index === 0 || index === data.length - 1) {
		return <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />;
	}
	return null;
};

export default SalesGrowth;
